import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Alert, Badge, Button, Input, Modal, Select, Spin, Table, Tabs, Tag, Tooltip, Typography
} from 'antd';
import {
    DeleteOutlined, ExclamationCircleOutlined, InfoCircleOutlined,
    ReloadOutlined, WarningOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import api from '../services/api';
import { useAuthGuard } from '../hooks/useAuthGuard';
import { useSidebar } from '../contexts/SidebarContext';
import { InfraUserCleanup, CleanupAlertRecord } from '../types';
import { useNewGui } from '../hooks/useNewGui';
import PageHeader from '../components/shared/PageHeader';
import { useRolePreview } from '../contexts/RolePreviewContext';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

const STAGE_TAG: Record<string, { color: string; label: string }> = {
    GRACE:       { color: 'blue',    label: 'Grace Period'  },
    ALERT_30D:   { color: 'geekblue', label: '30-Day Alert'  },
    ALERT_7D:    { color: 'orange',  label: '7-Day Alert'   },
    ALERT_1D:    { color: 'red',     label: 'Final Notice'  },
    HOME_DELETED:{ color: 'volcano', label: 'Home Archived' },
    COMPLETED:   { color: 'default', label: 'Completed'     },
};

const STAGE_TONE: Record<string, string> = {
    GRACE: 'info',
    ALERT_30D: 'accent',
    ALERT_7D: 'warning',
    ALERT_1D: 'error',
    HOME_DELETED: 'error',
    COMPLETED: 'success',
};

const CHIP_BASE = 'inline-block px-2 py-0.5 text-xs font-medium rounded border';

/** Map semantic tone names (shared with classic AntD Tag colors) to new-GUI token chip classes.
 *  Use solid token borders (not /30) so chips keep comparable weight to AntD Tag colors. */
function toneChipClass(tone: string): string {
    switch (tone) {
        case 'success':
            return 'bg-success/15 text-success border-success';
        case 'error':
            return 'bg-danger/15 text-danger border-danger';
        case 'warning':
            return 'bg-warning/15 text-warning border-warning';
        case 'info':
            return 'bg-info/15 text-info border-info';
        default:
            return 'bg-accent/15 text-accent border-accent';
    }
}

function TokenChip({ tone, children }: { tone: string; children: React.ReactNode }) {
    return (
        <span className={`${CHIP_BASE} ${toneChipClass(tone)}`}>
            {children}
        </span>
    );
}

function StageTag({ stage }: { stage: string | null }) {
    const useNew = useNewGui();
    if (!stage) return useNew ? <TokenChip tone="accent">-</TokenChip> : <Tag>-</Tag>;
    const s = STAGE_TAG[stage] ?? { color: 'default', label: stage };
    if (useNew) {
        return <TokenChip tone={STAGE_TONE[stage] ?? 'accent'}>{s.label}</TokenChip>;
    }
    return <Tag color={s.color}>{s.label}</Tag>;
}

function formatDate(val: string | null | undefined): string {
    if (!val) return '—';
    return new Date(val).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Tab 1 — Pipeline
// ---------------------------------------------------------------------------

function PipelineTab() {
    const [data, setData] = useState<InfraUserCleanup[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get<InfraUserCleanup[]>('/api/cleanup/pipeline');
            setData(d);
        } catch { setData([]); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const columns: ColumnsType<InfraUserCleanup> = [
        { title: 'User ID',   dataIndex: 'UserId',      key: 'UserId',      sorter: (a, b) => a.UserId.localeCompare(b.UserId) },
        { title: 'Server',    dataIndex: 'ServerInfo',  key: 'ServerInfo',  ellipsis: true },
        { title: 'Account Expiry', dataIndex: 'AccountExpiry', key: 'AccountExpiry',
          render: formatDate, sorter: (a, b) => (a.AccountExpiry ?? '').localeCompare(b.AccountExpiry ?? '') },
        { title: 'Stage',     dataIndex: 'cleanupStage', key: 'stage',
          render: (s) => <StageTag stage={s} />,
          filters: Object.keys(STAGE_TAG).filter(k => k !== 'COMPLETED').map(k => ({ text: STAGE_TAG[k].label, value: k })),
          onFilter: (v, r) => r.cleanupStage === v },
        { title: 'Scheduled Cleanup', dataIndex: 'cleanupScheduledDate', key: 'cleanupScheduledDate',
          render: formatDate, sorter: (a, b) => (a.cleanupScheduledDate ?? '').localeCompare(b.cleanupScheduledDate ?? '') },
        { title: 'Last Alert', dataIndex: 'lastCleanupAlertSentAt', key: 'lastAlerted', render: formatDate },
        { title: 'Email', dataIndex: 'Email', key: 'Email', ellipsis: true },
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-3">
                <Text type="secondary">{data.length} user(s) in pipeline</Text>
                <Button icon={<ReloadOutlined />} onClick={load} size="small">Refresh</Button>
            </div>
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                loading={loading}
                size="small"
                pagination={{ pageSize: 20 }}
                scroll={{ x: true }}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Tab 2 — Completed
// ---------------------------------------------------------------------------

function CompletedTab() {
    const [data, setData] = useState<InfraUserCleanup[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get<InfraUserCleanup[]>('/api/cleanup/completed');
            setData(d);
        } catch { setData([]); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const columns: ColumnsType<InfraUserCleanup> = [
        { title: 'User ID',  dataIndex: 'UserId',     key: 'UserId' },
        { title: 'Server',   dataIndex: 'ServerInfo', key: 'ServerInfo', ellipsis: true },
        { title: 'Account Expiry', dataIndex: 'AccountExpiry', key: 'AccountExpiry', render: formatDate },
        { title: 'Backup Location', dataIndex: 'backupLocation', key: 'backupLocation', ellipsis: true,
          render: (v) => v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : '—' },
        { title: 'Completed At', dataIndex: 'lastCleanupAlertSentAt', key: 'completedAt', render: formatDate },
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-3">
                <Text type="secondary">{data.length} user(s) cleaned up</Text>
                <Button icon={<ReloadOutlined />} onClick={load} size="small">Refresh</Button>
            </div>
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                loading={loading}
                size="small"
                pagination={{ pageSize: 20 }}
                scroll={{ x: true }}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Tab 3 — Alert Log
// ---------------------------------------------------------------------------

const TIER_COLORS: Record<string, string> = {
    GRACE_START:        'blue',
    ALERT_30D:          'geekblue',
    ALERT_7D:           'orange',
    ALERT_1D:           'red',
    HOME_DELETED:       'volcano',
    BACKUP_PURGED:      'purple',
    FORCE_DELETE_HOME:  'magenta',
    FORCE_PURGE_BACKUP: 'magenta',
    CLEANUP_CANCELLED:  'default',
};

const TIER_TONE: Record<string, string> = {
    GRACE_START: 'info',
    ALERT_30D: 'accent',
    ALERT_7D: 'warning',
    ALERT_1D: 'error',
    HOME_DELETED: 'error',
    BACKUP_PURGED: 'accent',
    FORCE_DELETE_HOME: 'error',
    FORCE_PURGE_BACKUP: 'error',
    CLEANUP_CANCELLED: 'accent',
};

function AlertLogTab() {
    const useNew = useNewGui();
    const [data, setData] = useState<CleanupAlertRecord[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get<CleanupAlertRecord[]>('/api/cleanup/alerts');
            setData(d);
        } catch { setData([]); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const columns: ColumnsType<CleanupAlertRecord> = [
        { title: 'Date', dataIndex: 'createdAt', key: 'createdAt',
          render: (v) => new Date(v).toLocaleString(),
          sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
          defaultSortOrder: 'descend', width: 170 },
        { title: 'User ID', dataIndex: 'userId', key: 'userId' },
        { title: 'Server', dataIndex: 'serverInfo', key: 'serverInfo', ellipsis: true },
        { title: 'Event', dataIndex: 'alertTier', key: 'alertTier',
          render: (v) => useNew
              ? <TokenChip tone={TIER_TONE[v] ?? 'accent'}>{v}</TokenChip>
              : <Tag color={TIER_COLORS[v] ?? 'default'}>{v}</Tag> },
        { title: 'Status', dataIndex: 'status', key: 'status',
          render: (v) => {
              if (useNew) {
                  const tone = v === 'FAILED' ? 'error' : v === 'SKIPPED' ? 'warning' : 'success';
                  return <TokenChip tone={tone}>{v}</TokenChip>;
              }
              return <Tag color={v === 'FAILED' ? 'red' : v === 'SKIPPED' ? 'orange' : 'green'}>{v}</Tag>;
          } },
        { title: 'Sent To', dataIndex: 'sentTo', key: 'sentTo', ellipsis: true },
        { title: 'By', dataIndex: 'initiatedBy', key: 'initiatedBy', ellipsis: true },
        { title: 'Notes', dataIndex: 'notes', key: 'notes', ellipsis: true },
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-3">
                <Text type="secondary">{data.length} log entries (latest 500)</Text>
                <Button icon={<ReloadOutlined />} onClick={load} size="small">Refresh</Button>
            </div>
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                loading={loading}
                size="small"
                pagination={{ pageSize: 25 }}
                scroll={{ x: true }}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Tab 4 — Force Actions (superadmin only)
// ---------------------------------------------------------------------------

type ForceAction = 'delete-home' | 'purge-backup' | 'cancel';

const ACTION_META: Record<ForceAction, { label: string; danger: boolean; description: string }> = {
    'delete-home':   { label: 'Force Archive Home', danger: true,
        description: 'Immediately move the user\'s home directory to the backup location via SSH.' },
    'purge-backup':  { label: 'Force Purge Backup', danger: true,
        description: 'Permanently delete the archived backup. This cannot be undone.' },
    'cancel':        { label: 'Cancel Cleanup', danger: false,
        description: 'Remove the user from the cleanup pipeline (reset all cleanup fields).' },
};

function ForceActionsTab() {
    const useNew = useNewGui();
    const [pipeline, setPipeline] = useState<InfraUserCleanup[]>([]);
    const [loadingPipeline, setLoadingPipeline] = useState(true);

    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [action, setAction] = useState<ForceAction>('delete-home');
    const [confirmedUserId, setConfirmedUserId] = useState('');
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

    useEffect(() => {
        setLoadingPipeline(true);
        api.get<InfraUserCleanup[]>('/api/cleanup/pipeline')
            .then(({ data }) => setPipeline(data))
            .catch(() => setPipeline([]))
            .finally(() => setLoadingPipeline(false));
    }, []);

    const selectedUser = pipeline.find(u => u.id === selectedId);
    const isUnlocked = confirmedUserId.trim() === selectedUser?.UserId;
    const canSubmit = !!selectedId && isUnlocked && reason.trim().length > 5 && !submitting;

    const handleSubmit = async () => {
        if (!selectedUser || !canSubmit) return;
        setSubmitting(true);
        setResult(null);
        try {
            const { data } = await api.post<{ message: string }>('/api/cleanup/force-action', {
                action,
                infraUserId: selectedUser.id,
                confirmedUserId: confirmedUserId.trim(),
                reason: reason.trim(),
            });
            setResult({ type: 'success', msg: data.message });
            setConfirmedUserId('');
            setReason('');
            setSelectedId(null);
            // Reload pipeline
            const fresh = await api.get<InfraUserCleanup[]>('/api/cleanup/pipeline');
            setPipeline(fresh.data);
        } catch (err: any) {
            setResult({ type: 'error', msg: err?.response?.data?.message || err.message });
        } finally {
            setSubmitting(false);
        }
    };

    if (loadingPipeline) return <Spin tip="Loading pipeline..." />;

    return (
        <div style={{ maxWidth: 640 }}>
            <Alert
                type="error"
                showIcon
                icon={<WarningOutlined />}
                message="Superadmin Force Actions"
                description="Actions here bypass the nightly schedule and execute immediately via SSH. Ensure you understand the impact before proceeding."
                className="mb-6"
            />

            <div className={useNew
                ? 'bg-surface border-2 border-border-strong rounded p-5 space-y-4 shadow-sm'
                : 'bg-white rounded-lg shadow-sm p-5 space-y-4'}>

                {/* Select user */}
                <div>
                    <Text strong>Select User</Text>
                    <Select
                        showSearch
                        style={{ display: 'block', marginTop: 6 }}
                        placeholder="Select user from cleanup pipeline"
                        value={selectedId ?? undefined}
                        onChange={(v) => { setSelectedId(v); setConfirmedUserId(''); }}
                        options={pipeline.map(u => ({
                            value: u.id,
                            label: `${u.UserId} — ${u.ServerInfo} [${u.cleanupStage}]`,
                        }))}
                        filterOption={(input, option) =>
                            (option?.label as string).toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </div>

                {/* Pre-action summary card */}
                {selectedUser && (
                    <div className={useNew
                        ? 'bg-surface-raised rounded p-3 text-sm border-2 border-border-strong'
                        : 'bg-gray-50 rounded p-3 text-sm border border-gray-200'}>
                        <div className="grid grid-cols-2 gap-y-1">
                            <Text type="secondary">User ID:</Text><Text strong>{selectedUser.UserId}</Text>
                            <Text type="secondary">Server:</Text><Text>{selectedUser.ServerInfo}</Text>
                            <Text type="secondary">Stage:</Text><StageTag stage={selectedUser.cleanupStage} />
                            <Text type="secondary">Scheduled Date:</Text><Text>{formatDate(selectedUser.cleanupScheduledDate)}</Text>
                            <Text type="secondary">Home Dir:</Text><Text code style={{ fontSize: 11 }}>{selectedUser.HomeDir || `/home/${selectedUser.UserId}`}</Text>
                            {selectedUser.backupLocation && (
                                <><Text type="secondary">Backup:</Text><Text code style={{ fontSize: 11 }}>{selectedUser.backupLocation}</Text></>
                            )}
                        </div>
                    </div>
                )}

                {/* Action select */}
                <div>
                    <Text strong>Action</Text>
                    <Select
                        style={{ display: 'block', marginTop: 6 }}
                        value={action}
                        onChange={(v) => setAction(v as ForceAction)}
                        options={Object.entries(ACTION_META).map(([k, v]) => ({
                            value: k, label: v.label,
                        }))}
                    />
                    <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                        {ACTION_META[action].description}
                    </Text>
                </div>

                {/* Reason */}
                <div>
                    <Text strong>Reason <Text type="danger">*</Text></Text>
                    <Input.TextArea
                        rows={2}
                        style={{ marginTop: 6 }}
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        placeholder="Mandatory: describe why this action is necessary (min 6 chars)"
                    />
                </div>

                {/* Confirmation friction */}
                {selectedUser && (
                    <div>
                        <Text strong>
                            Type user ID to confirm:&nbsp;
                            <Text code>{selectedUser.UserId}</Text>
                        </Text>
                        <Input
                            style={{ marginTop: 6 }}
                            value={confirmedUserId}
                            onChange={e => setConfirmedUserId(e.target.value)}
                            placeholder={`Type "${selectedUser.UserId}" to unlock`}
                            status={confirmedUserId && !isUnlocked ? 'error' : ''}
                        />
                        {confirmedUserId && !isUnlocked && (
                            <Text type="danger" style={{ fontSize: 12 }}>User ID does not match</Text>
                        )}
                    </div>
                )}

                {result && (
                    <Alert
                        type={result.type}
                        message={result.msg}
                        showIcon
                        closable
                        onClose={() => setResult(null)}
                    />
                )}

                <Button
                    danger={ACTION_META[action].danger}
                    type="primary"
                    disabled={!canSubmit}
                    loading={submitting}
                    onClick={handleSubmit}
                    block
                >
                    {ACTION_META[action].label}
                </Button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export default function CleanupTrackingPage() {
    const { user } = useAuthGuard();
    const { isSidebarOpen } = useSidebar();
    const { effectiveRole } = useRolePreview();
    const role = effectiveRole || user?.role;
    const isSuperadmin = role === 'superadmin';
    const useNew = useNewGui();

    if (!user || !role || !['itadmin', 'superadmin'].includes(role)) {
        return (
            <div className={`flex-1 p-6 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
                <Alert type="error" message="Access denied. IT Admin or Superadmin role required." showIcon />
            </div>
        );
    }

    return (
        <div className={`flex-1 p-6 transition-all duration-300 ${useNew ? 'bg-surface-app font-plex' : 'bg-gray-50'} ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
            {useNew ? (
                <PageHeader title="Cleanup Tracking" purpose="Monitor the automated infra-user lifecycle: grace period → alerts → home archival → backup purge." />
            ) : (
                <>
                    <h1 className="text-3xl font-semibold text-gray-800 mb-1">Cleanup Tracking</h1>
                    <p className="text-sm text-gray-500 mb-5">
                        Monitor the automated infra-user lifecycle: grace period → alerts → home archival → backup purge.
                    </p>
                </>
            )}

            <div className={useNew
                ? 'bg-surface border-2 border-border-strong rounded p-5 shadow-sm'
                : 'bg-white rounded-lg shadow-sm p-5'}>
                <Tabs defaultActiveKey="pipeline" type="card">
                    <TabPane tab="In Pipeline" key="pipeline">
                        <PipelineTab />
                    </TabPane>
                    <TabPane tab="Completed" key="completed">
                        <CompletedTab />
                    </TabPane>
                    <TabPane tab="Alert Log" key="alerts">
                        <AlertLogTab />
                    </TabPane>
                    {isSuperadmin && (
                        <TabPane
                            tab={
                                <span>
                                    <WarningOutlined style={{ color: useNew ? '#C0392B' : '#cf1322' }} />
                                    {' '}Force Actions
                                </span>
                            }
                            key="force"
                        >
                            <ForceActionsTab />
                        </TabPane>
                    )}
                </Tabs>
            </div>
        </div>
    );
}
